import React, { useCallback } from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage } from 'react-intl';
import { useSelector } from 'react-redux';

import { Button } from 'ui-components/components/button';
import { Modal } from 'ui-components/components/modal';
import { ModalBody } from 'ui-components/components/modal/modal-body';
import { ModalFooter } from 'ui-components/components/modal/modal-footer';
import { ModalHeader } from 'ui-components/components/modal/modal-header';
import { Text, TextType } from 'ui-components/components/text';
import { useAsyncSafeState } from 'ui-components/hooks/async-safe';

import { useLocalizedPlanName } from './components/plan-name-helper';
import { useManageSubscriptionDrawerContext } from './context/manage-subscription-drawer-context';
import { DeactivateUserButtonValue } from './deactivate-user-button';
import { useGetUserSeatsAddOnData } from './hooks/use-get-user-seats-addon-data';
import { useSelectedPlan } from './hooks/use-plan-details';
import { messages } from './messages';
import style from './style.module.less';
import userAddOnModalStyle from './user-add-on-modal.module.css';
import { FormattedCurrency } from '../../../components/formatted-currency';
import { UserDateFormat } from '../../../components/user-date-format/index';
import { useGlobalModalLaunchContext } from '../../../contexts/modal-context/global-modal-context';
import Global from '../../../store/global-selectors';

export const UserAddOnModal = ({ onClose }) => {
  const { openManageSubscriptionDrawer } = useGlobalModalLaunchContext();
  const { desiredPlanCode } = useManageSubscriptionDrawerContext();
  const subscriptionData = useSelector(Global.selectSellerSubscriptionData);
  const { code: currentlySelectedPlanCode } = useSelectedPlan();
  const selectedPlanCode = desiredPlanCode || currentlySelectedPlanCode;
  const { allPlans = [], planToNonWalletAccessPlanMap = {} } = subscriptionData ?? {};
  const desiredPlan =
    allPlans.find((plan) => plan.code === selectedPlanCode) ||
    Object.values(planToNonWalletAccessPlanMap).find((plan) => plan.code === selectedPlanCode) ||
    {};
  const { userLimit: { limit: desiredPlanUserLimit = '' } = {} } = desiredPlan;

  const users = useSelector(Global.selectSellerUsers);
  const activeUsers = users.reduce((acc, user) => {
    if (user.isActive) {
      return {
        ...acc,
        [user.userId]: user,
      };
    }
    return acc;
  }, {});

  const [activeUsersObject, setActiveUsersObject] = useAsyncSafeState(activeUsers);
  const numberOfActiveUsers = Object.keys(activeUsers).length;

  const closeUpdateAddOnsModal = useCallback(() => {
    onClose();
    openManageSubscriptionDrawer({ selectedPlanId: desiredPlan.id, desiredNumberOfAdditionalUsers: desiredPlanUserLimit });
  }, [onClose, openManageSubscriptionDrawer, desiredPlan.id, desiredPlanUserLimit]);

  const { unitCost } = useGetUserSeatsAddOnData();
  const onClick = closeUpdateAddOnsModal;
  const desiredPlanName = useLocalizedPlanName(desiredPlan?.name);
  const headerMessage = <FormattedMessage {...messages.manageUsersAddOnHeader} />;
  const bodyMessage = (
    <Text
      formattedMessageValues={{
        planName: desiredPlanName,
        userCount: desiredPlanUserLimit,
        price: <FormattedCurrency code={unitCost.code} value={unitCost.value} />,
        b: (chunks) => <b>{chunks}</b>,
      }}
      textType={TextType.Paragraph}
      value={messages.howManySeats}
    />
  );

  const buttonMessage = <FormattedMessage {...messages.save} />;
  return (
    <Modal className={style['modal-base']} size='medium'>
      <ModalHeader closeModal={closeUpdateAddOnsModal}>{headerMessage}</ModalHeader>
      <ModalBody className={style['modal-body']}>
        {bodyMessage}
        <div className={userAddOnModalStyle['user-limit-description']} />
        <div className={userAddOnModalStyle['active-user-heading']}>
          <Text formattedMessageValues={{ userCount: numberOfActiveUsers }} textType={TextType.H4} value={messages.additionalUsersList} />
        </div>
        <table className={userAddOnModalStyle['active-user-grid']}>
          <thead>
            <tr>
              <th>
                <Text textType={TextType.Label} value={messages.userName} />
              </th>
              <th>
                <Text textType={TextType.Label} value={messages.userLastLoginDate} />
              </th>
              <th> </th>
            </tr>
          </thead>
          <tbody>
            {Object.values(activeUsersObject).map((activeUser) => (
              <tr key={`${activeUser.userId}-0`} className={userAddOnModalStyle[`${activeUser.isActive ? '' : 'row-deactivated'}`]}>
                <td key={`${activeUser.userId}-1`}>{activeUser.name}</td>
                <td key={`${activeUser.userId}-2`}>
                  <UserDateFormat value={activeUser.lastLoginDate} />
                </td>
                <td key={`${activeUser.userId}-3`}>
                  <DeactivateUserButtonValue setActiveUsersObject={setActiveUsersObject} user={activeUser} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </ModalBody>
      <ModalFooter>
        <Button onClick={onClick} theme='primary'>
          {buttonMessage}
        </Button>
      </ModalFooter>
    </Modal>
  );
};

UserAddOnModal.propTypes = {
  onClose: PropTypes.func.isRequired,
};
